import colorNameList from "color-name-list";
import nearestColor from "nearest-color";
import translate from "translate";

/**
 * Converts a File object to a base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} A promise that resolves with the base64 string
 *
 * Uses FileReader to:
 * 1. Read file as Data URL
 * 2. Extract pure base64 string by removing data URL prefix
 */
export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const fullResult = reader.result as string;
      const base64String = fullResult.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) =>
      reject(new Error(error instanceof Error ? error.message : String(error)));
  });
};

/**
 * Checks if a string is valid base64
 * @param {string} str - String to validate
 * @returns {boolean} True if string is valid base64
 *
 * Attempts to encode/decode the string to verify base64 format
 */
export const isBase64 = (str: string): boolean => {
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
};

/**
 * Gets the translated color name from a hex value
 * @param {string} hex - Hex color code
 * @returns {Promise<string>} Translated color name in Spanish
 *
 * Process:
 * 1. Finds nearest color match from predefined list
 * 2. Gets English color name
 * 3. Translates to Spanish
 * 4. Falls back to English name or error message if translation fails
 */
export const getColorName = async (hex: string) => {
  const colors = colorNameList.reduce(
    (o, { name, hex }) => Object.assign(o, { [name]: hex }),
    {}
  );
  const nearest = nearestColor.from(colors);
  const englishName = nearest(hex)?.name;
  if (!englishName) {
    return "Color no detectado";
  }
  try {
    const spanishName = await translate(englishName, {
      from: "en",
      to: "es",
    });
    return spanishName;
  } catch (error) {
    console.error("Translation error:", error);
    return englishName;
  }
};

/**
 * Obtiene la imagen correcta que corresponde al color específico del producto
 * @param images - Array de URLs de imágenes
 * @param productColor - Color del producto (hex code)
 * @returns URL de la imagen que corresponde al color, o la primera imagen si no se encuentra
 * 
 * @example
 * // Para un producto con color "#f4abc8" (Rosilla)
 * const images = [
 *   "http://127.0.0.1:8000/storage/images/products/18_img2_a3c6c4.png",
 *   "http://127.0.0.1:8000/storage/images/products/18_img3_f4abc8.png"
 * ];
 * getImageByColor(images, "#f4abc8") // Retorna la imagen que contiene "f4abc8"
 */
export const getImageByColor = (images: string[], productColor: string): string => {
  if (!images || images.length === 0) {
    return "";
  }

  // Si solo hay una imagen, devolverla
  if (images.length === 1) {
    return images[0];
  }

  // Buscar una imagen que contenga el color en el nombre del archivo
  const colorImage = images.find(image => {
    const fileName = image.split('/').pop() || '';
    return fileName.includes(productColor.replace('#', ''));
  });

  // Si se encuentra una imagen con el color, devolverla
  if (colorImage) {
    return colorImage;
  }

  // Si no se encuentra, devolver la primera imagen como fallback
  return images[0];
};

/**
 * Obtiene la imagen correcta para un producto de orden específico
 * @param orderProduct - Producto de la orden con imágenes y color
 * @returns URL de la imagen que corresponde al color del producto
 */
export const getOrderProductImage = (orderProduct: {
  images: string[];
  color: string;
}): string => {
  return getImageByColor(orderProduct?.images, orderProduct?.color);
};
